import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { PlaceDetail } from '../../../../services/locations.service';
import { EditorHostService } from './editor-host.service';
import { AreasSaved, MapEditsService } from './map-edits.service';
import { PageEditorService } from './page-editor.service';
import { SuburbTab } from './panel';
import { SubRegionsService } from './subregions.service';
import { SuburbDetailService } from './suburb-detail.service';
import { SuburbPlacesService } from './suburb-places.service';
import { WorkspaceStateService } from './workspace-state.service';

/**
 * Where the side panel goes (#256, #253, #260, #261): a suburb (and its tabs), one of its places, a place added by
 * hand, a sub-region, Regions, "Places and other pages", Waiting for review. Each move first asks before discarding
 * a mounted editor, then loads what the panel shows and keeps the map in step (the selected suburb, the places, the
 * part of the map the panel covers). The open panel reloads after a map save that touched it.
 */
@Injectable()
export class PanelNavService implements OnDestroy {
  private panelEl: HTMLElement | null = null;
  private saved: Subscription;

  constructor(
    private state: WorkspaceStateService,
    private editor: EditorHostService,
    private detail: SuburbDetailService,
    private places: SuburbPlacesService,
    private subRegions: SubRegionsService,
    private pageEditor: PageEditorService,
    edits: MapEditsService
  ) {
    this.saved = edits.saved$.subscribe((e) => this.reloadAfterSave(e));
  }

  ngOnDestroy(): void {
    this.saved.unsubscribe();
  }

  /** The side panel's element: its width is the part of the map it covers. */
  attachPanel(el: HTMLElement | null): void {
    this.panelEl = el;
  }

  /**
   * Open (or switch) the panel to one suburb, on `tab` (the readiness view opens the tab that fixes a gap), else the
   * tab you were on unless an editor was open.
   */
  openSuburb(code: string, tab?: SuburbTab): void {
    if (!this.editor.leave()) return;
    const panel = this.state.panel;
    const keep = panel?.kind === 'suburb' ? panel.tab : 'overview';
    const before = panel?.kind === 'suburb' || panel?.kind === 'place' ? panel.salCode : null;
    this.state.panel = {
      kind: 'suburb',
      salCode: code,
      tab: tab ?? (keep === 'page' ? 'overview' : keep),
    };
    const engine = this.state.engine;
    this.places.closePopup();
    this.places.endPin();
    engine.select(code);
    engine.selectPlace(null);
    this.syncInset();
    // A newly selected suburb is zoomed into the part of the map the panel leaves clear, so its places can be seen.
    if (code !== before) engine.flyToFeature(code);
    this.detail.load(code);
    this.places.load(code);
    if (tab === 'page') this.pageEditor.openPageTab();
  }

  setTab(tab: SuburbTab): void {
    const panel = this.state.panel;
    if (panel?.kind !== 'suburb') return;
    if (!this.editor.leave()) return;
    this.state.panel = { ...panel, tab };
    if (tab === 'page') this.pageEditor.openPageTab();
  }

  closePanel(): void {
    if (!this.editor.leave()) return;
    this.state.panel = null;
    this.detail.clear();
    this.state.engine.select(null);
    this.places.closePopup();
    this.places.endPin();
    this.places.clear();
    this.syncInset();
  }

  /** Fly to a suburb (loading its state) and open its panel. */
  flyTo(code: string): void {
    const state = this.state;
    if (state.view !== 'map') state.view = 'map';
    if (state.panel) this.syncInset();
    state.engine.flyToCode(code).then(() => {
      state.stateCode = state.engine.stateCode ?? state.stateCode;
      state.engine.select(code);
      this.openSuburb(code);
    });
  }

  /** One sub-region's panel: its members, its website page, Fill from council areas, its gate, colour, Delete. */
  openSubRegion(id: string): void {
    if (!this.editor.leave()) return;
    this.state.panel = { kind: 'subregion', id };
    this.places.closePopup();
    this.places.endPin();
    this.state.engine.select(null);
    this.syncInset();
    this.subRegions.reset();
    this.subRegions.load(id, true);
    this.state.engine.flyToSubRegion(id);
  }

  openRegions(): void {
    if (!this.editor.leave()) return;
    this.state.panel = { kind: 'regions' };
  }

  openOtherPages(): void {
    if (!this.editor.leave()) return;
    this.state.panel = { kind: 'other-pages' };
  }

  /** Places waiting for review to go on the website: asked for by the team, or suggested by the AI (#261). */
  openPending(): void {
    if (!this.editor.leave()) return;
    this.places.closePopup();
    this.state.panel = { kind: 'pending' };
  }

  // ------------------------------------------------------------------ places (#253 step 3)

  /** A place from the map or the Places tab: its panel, with the way back to the suburb. */
  openPlace(id: number): void {
    if (!this.editor.leave()) return;
    this.places.endPin();
    const panel = this.state.panel;
    const salCode =
      panel?.kind === 'suburb' || panel?.kind === 'place' ? panel.salCode : this.places.code;
    if (!salCode) return;
    this.state.panel = { kind: 'place', salCode, locationId: id };
    this.state.engine.selectPlace(id);
    this.syncInset();
    this.state.redraw();
  }

  backToSuburb(): void {
    const panel = this.state.panel;
    if (panel?.kind !== 'place') return;
    this.places.endPin();
    this.state.engine.selectPlace(null);
    this.state.panel = { kind: 'suburb', salCode: panel.salCode, tab: 'places' };
    this.state.redraw();
  }

  /** Add a place that isn't in the crawl: the form in the panel, the pin at the suburb's centre to start. */
  startAddPlace(): void {
    const panel = this.state.panel;
    const salCode = panel?.kind === 'suburb' ? panel.salCode : this.places.code;
    if (!salCode || this.state.readOnly) return;
    this.places.closePopup();
    this.state.panel = { kind: 'place-new', salCode, title: 'Add a place' };
    const c = this.detail.detail?.suburb?.centroid;
    this.places.startPin(Array.isArray(c) ? { lng: +c[0], lat: +c[1] } : null);
    this.state.redraw();
  }

  /** A place added by hand: its details and pin in the form. */
  editCustomPlace(place: PlaceDetail): void {
    const panel = this.state.panel;
    const salCode = panel?.kind === 'place' ? panel.salCode : this.places.code;
    if (!salCode || !place.custom) return;
    this.state.panel = {
      kind: 'place-edit',
      salCode,
      locationId: place.locationId,
      place,
      title: `Edit ${place.crawlName}`,
    };
    this.places.startPin(place.position);
    this.state.redraw();
  }

  customSaved(d: PlaceDetail): void {
    const panel = this.state.panel;
    const salCode =
      panel?.kind === 'place-new' || panel?.kind === 'place-edit'
        ? panel.salCode
        : this.places.code;
    this.places.endPin();
    if (!salCode) return;
    this.state.setStatus(`${d.name} saved.`);
    this.places.load(salCode, true);
    this.state.panel = { kind: 'place', salCode, locationId: d.locationId };
    this.state.engine.selectPlace(d.locationId);
    this.state.redraw();
  }

  customCancelled(): void {
    const panel = this.state.panel;
    this.places.endPin();
    if (panel?.kind === 'place-edit')
      this.state.panel = { kind: 'place', salCode: panel.salCode, locationId: panel.locationId };
    else if (panel?.kind === 'place-new')
      this.state.panel = { kind: 'suburb', salCode: panel.salCode, tab: 'places' };
    this.state.redraw();
  }

  customDeleted(): void {
    const panel = this.state.panel;
    const salCode = panel?.kind === 'place-edit' ? panel.salCode : this.places.code;
    this.places.endPin();
    if (!salCode) return;
    this.state.setStatus('Place deleted.');
    this.places.load(salCode, true);
    this.state.panel = { kind: 'suburb', salCode, tab: 'places' };
    this.state.redraw();
  }

  /** Tell the map how much of it the panel covers, so fitting a suburb or region keeps clear of it. */
  syncInset(): void {
    this.state.engine.rightInset = this.state.panel ? this.panelEl?.offsetWidth || 0 : 0;
  }

  /** A map save or undo: reload the open suburb (when it was touched) or sub-region. */
  private reloadAfterSave(e: AreasSaved): void {
    const panel = this.state.panel;
    if (panel?.kind === 'suburb' && (!e.codes || e.codes.includes(panel.salCode)))
      this.detail.load(panel.salCode);
    if (panel?.kind === 'subregion') this.subRegions.load(panel.id);
  }
}
